import { supabase } from '../lib/supabase';

/**
 * Get all ICS subscriptions for the current user
 */
export const getIcsSubscriptions = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('ics_subscriptions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching ICS subscriptions:', error);
    throw error;
  }
};

/**
 * Add a new ICS subscription
 * @param {string} name - User-friendly name for the calendar
 * @param {string} url - URL of the ICS feed
 */
export const addIcsSubscription = async (name, url) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Validate URL format
    if (!isValidUrl(url)) {
      throw new Error('Invalid URL format. Please enter a valid calendar URL.');
    }

    // Insert the subscription
    const { data, error } = await supabase
      .from('ics_subscriptions')
      .insert({
        user_id: user.id,
        name: name.trim(),
        url: url.trim(),
        sync_enabled: true,
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('You have already added this calendar URL.');
      }
      throw error;
    }

    // Trigger initial sync
    try {
      await syncIcsCalendar(data.id);
    } catch (syncError) {
      console.warn('Initial sync failed:', syncError);
      // Don't fail the add operation if sync fails
    }

    return {
      success: true,
      subscription: data,
      message: `Successfully added "${name}" calendar`,
    };
  } catch (error) {
    console.error('Error adding ICS subscription:', error);
    return {
      success: false,
      error: error.message || 'Failed to add calendar subscription',
    };
  }
};

/**
 * Remove an ICS subscription
 * @param {string} subscriptionId - UUID of the subscription to remove
 */
export const removeIcsSubscription = async (subscriptionId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First, delete the associated availability records
    const { error: availabilityError } = await supabase
      .from('user_availability')
      .delete()
      .eq('user_id', user.id)
      .eq('calendar_provider', 'ics')
      .like('calendar_event_id', `ics:${subscriptionId}:%`);

    if (availabilityError) {
      console.warn('Error deleting availability records:', availabilityError);
    }

    // Then delete the subscription
    const { error } = await supabase
      .from('ics_subscriptions')
      .delete()
      .eq('id', subscriptionId)
      .eq('user_id', user.id);

    if (error) throw error;

    return {
      success: true,
      message: 'Calendar subscription removed',
    };
  } catch (error) {
    console.error('Error removing ICS subscription:', error);
    return {
      success: false,
      error: error.message || 'Failed to remove calendar subscription',
    };
  }
};

/**
 * Sync a specific ICS calendar
 * @param {string} subscriptionId - UUID of the subscription to sync
 */
export const syncIcsCalendar = async (subscriptionId) => {
  try {
    // Get sync settings
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(today.getDate() + 180); // Sync next 180 days

    const { data, error } = await supabase.functions.invoke('ics-sync-calendar', {
      body: {
        action: 'sync',
        subscriptionId,
        startDate: today.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        syncSettings: {
          excludeAllDayEvents: false,
          excludeCancelledEvents: true,
          onlySyncBusyEvents: true, // Only sync events marked as busy (uses X-MICROSOFT-CDO-INTENDEDSTATUS first)
        },
      },
    });

    if (error) {
      throw new Error(error.message || 'Sync failed');
    }

    return {
      success: true,
      eventsProcessed: data.eventsProcessed || 0,
      message: data.message || 'Calendar synced successfully',
    };
  } catch (error) {
    console.error('Error syncing ICS calendar:', error);
    return {
      success: false,
      error: error.message || 'Failed to sync calendar',
    };
  }
};

/**
 * Sync all ICS calendars for the current user
 */
export const syncAllIcsCalendars = async () => {
  try {
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(today.getDate() + 180);

    const { data, error } = await supabase.functions.invoke('ics-sync-calendar', {
      body: {
        action: 'syncAll',
        startDate: today.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        syncSettings: {
          excludeAllDayEvents: false,
          excludeCancelledEvents: true,
          onlySyncBusyEvents: true, // Only sync events marked as busy (uses X-MICROSOFT-CDO-INTENDEDSTATUS first)
        },
      },
    });

    if (error) {
      throw new Error(error.message || 'Sync all failed');
    }

    return {
      success: true,
      results: data.results,
      message: data.message || 'All calendars synced',
    };
  } catch (error) {
    console.error('Error syncing all ICS calendars:', error);
    return {
      success: false,
      error: error.message || 'Failed to sync calendars',
    };
  }
};

/**
 * Update an ICS subscription (name or sync_enabled)
 * @param {string} subscriptionId - UUID of the subscription
 * @param {object} updates - Fields to update
 */
export const updateIcsSubscription = async (subscriptionId, updates) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('ics_subscriptions')
      .update(updates)
      .eq('id', subscriptionId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      subscription: data,
    };
  } catch (error) {
    console.error('Error updating ICS subscription:', error);
    return {
      success: false,
      error: error.message || 'Failed to update subscription',
    };
  }
};

/**
 * Check if any ICS subscriptions exist for the current user
 */
export const hasIcsSubscriptions = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { count, error } = await supabase
      .from('ics_subscriptions')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id);

    if (error) throw error;
    return count > 0;
  } catch (error) {
    console.error('Error checking ICS subscriptions:', error);
    return false;
  }
};

/**
 * Validate URL format
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
function isValidUrl(url) {
  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch {
    return false;
  }
}

/**
 * Get a helpful hint about where to find ICS URLs for popular calendar providers
 */
export const getIcsUrlHints = () => {
  return {
    apple: {
      name: 'Apple Calendar (iCloud)',
      instructions: 'In Apple Calendar app: Calendar > Share Calendar > Public Calendar, then copy the URL',
    },
    google: {
      name: 'Google Calendar',
      instructions: 'In Google Calendar: Settings > [Calendar Name] > Integrate calendar > Secret address in iCal format',
    },
    outlook: {
      name: 'Outlook.com',
      instructions: 'In Outlook.com: Calendar Settings > Shared calendars > Publish a calendar, select ICS format',
    },
    general: {
      name: 'Other Calendars',
      instructions: 'Look for "Subscribe", "Share", or "Export" options in your calendar app and find the ICS/iCal URL',
    },
  };
};
